/* eslint-disable react/prop-types */
import React, { useContext, useEffect } from 'react';
import type { ELSModalService } from '@els/els-component-modal-react';
import {
  ELSCheckBox,
  ELSDate,
  ELSDropDownOption
} from '@els/els-component-form-field-react';
import { ELSButton } from '@els/els-component-button-react';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  RecommendationAttemptStatusDto,
  RecommendationContentTypeDto,
  RemediationActivityTypeDto,
  StatusWithDueDate
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { FlexItem } from '../../components/flex/FlexItem.component';
import {
  ELSDropDown,
  ELSIcon
} from '../../components/els.components';
import {
  ActivityTypeConfigMap,
  RemediationContentTypeConfigMap
} from '../../models/remediation.models';
import IsRender from '../../components/is-render/IsRender.component';
import {
  ActivityNameFilterDefaultTitle,
} from './remediation-student-activity.constants';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import {
  getActiveAssignmentActivityTypeSettings,
  getActiveContentTypes,
  getAssessmentGoals,
  getCategoryGroupNameForGoal,
  getGoalBreadcrumbs,
  getRecommendationFromAttempt
} from '../../utilities/remediation.utilities';
import { StatusDisplayMap } from '../../constants/remediation.constants';
import { getQuizSubmissionCount } from './remediation-student-activity.utilities';
import { MIN_YEAR } from '../../constants/date.constants';
import { defaultActivityLogState } from './ActivityLog.component';

export const ActivityLogFilterModalId = 'ActivityLogFilterModalId';

export type ActivityLogFilterModalProps = {
  modalService: ELSModalService;
  selectedActivityTypes: RemediationActivityTypeDto[];
  selectedContentTypes: RecommendationContentTypeDto[];
  selectedGoalId: string;
  selectedStatuses: StatusWithDueDate[];
  startDate: Date;
  endDate: Date;
  handleApplyFilters: (props: {
    selectedActivityTypes: RemediationActivityTypeDto[];
    selectedContentTypes: RecommendationContentTypeDto[];
    selectedStatuses: StatusWithDueDate[];
    selectedGoalId: string;
    startDate: Date;
    endDate: Date;
  }) => void;
};

export type ActivityLogFilterModalState = {
  selectedActivityTypes: RemediationActivityTypeDto[];
  selectedContentTypes: RecommendationContentTypeDto[];
  selectedStatuses: StatusWithDueDate[];
  isActivityTypeExpanded: boolean;
  isContentTypeExpanded: boolean;
  isStatusExpanded: boolean;
  selectedGoalId: string;
  startDate: Date;
  endDate: Date;
}

const defaultState: ActivityLogFilterModalState = {
  selectedActivityTypes: [],
  selectedContentTypes: [],
  selectedStatuses: [],
  isActivityTypeExpanded: true,
  isContentTypeExpanded: true,
  isStatusExpanded: true,
  selectedGoalId: ActivityNameFilterDefaultTitle,
  startDate: null,
  endDate: null,
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const ActivityLogFilterModal = (props: ActivityLogFilterModalProps): JSX.Element => {
  const [state, setState] = React.useState<ActivityLogFilterModalState>(defaultState);
  const { appState } = useContext(AppContext);
  const baseState = appState.remediationBaseState;

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      selectedActivityTypes: props.selectedActivityTypes,
      selectedContentTypes: props.selectedContentTypes,
      selectedStatuses: props.selectedStatuses,
      selectedGoalId: props.selectedGoalId,
      startDate: props.startDate,
      endDate: props.endDate,
    }));
  }, [props]);

  const handleCancelClick = () => {
    props.modalService.closeModal(ActivityLogFilterModalId);
  };

  const handleActivityTypeExpandToggle = () => {
    setState((prevState) => ({
      ...prevState,
      isActivityTypeExpanded: !state.isActivityTypeExpanded,
    }));
  };

  const handleContentTypeExpandToggle = () => {
    setState((prevState) => ({
      ...prevState,
      isContentTypeExpanded: !state.isContentTypeExpanded,
    }));
  };

  const handleStatusExpandToggle = () => {
    setState((prevState) => ({
      ...prevState,
      isStatusExpanded: !state.isStatusExpanded,
    }));
  };

  const handleActivityTypeFilterSelect = (selectedActivityType: RemediationActivityTypeDto) => {
    const { selectedActivityTypes } = state;

    const isSelected = state.selectedActivityTypes.includes(selectedActivityType);
    const updatedSelectedActivityTypes = isSelected
      ? selectedActivityTypes.filter((activityType) => activityType !== selectedActivityType)
      : [...selectedActivityTypes, selectedActivityType];

    setState((prevState) => ({
      ...prevState,
      selectedActivityTypes: updatedSelectedActivityTypes,
    }));
  };

  const handleContentTypeFilterSelect = (selectedContentType: RecommendationContentTypeDto) => {
    const { selectedContentTypes } = state;

    const isSelected = selectedContentTypes.includes(selectedContentType);
    const updatedSelectedContentTypes = isSelected
      ? selectedContentTypes.filter((contentType) => contentType !== selectedContentType)
      : [...selectedContentTypes, selectedContentType];

    setState((prevState) => ({
      ...prevState,
      selectedContentTypes: updatedSelectedContentTypes,
    }));
  };

  const handleStatusFilterSelect = (selectedStatus: StatusWithDueDate) => {
    const { selectedStatuses } = state;

    const isSelected = state.selectedStatuses.includes(selectedStatus);
    const updatedSelectedStatuses = isSelected
      ? selectedStatuses.filter((activityType) => activityType !== selectedStatus)
      : [...selectedStatuses, selectedStatus];

    setState((prevState) => ({
      ...prevState,
      selectedStatuses: updatedSelectedStatuses,
    }));
  };

  const handleActivityNameFilterSelect = (selectedGoalId: string) => {
    if (!selectedGoalId) {
      return;
    }

    if (selectedGoalId === ActivityNameFilterDefaultTitle) {
      setState((prevState) => ({
        ...prevState,
        selectedGoalId: null,
      }));
      return;
    }

    setState((prevState) => ({
      ...prevState,
      selectedGoalId,
    }));
  };

  const handleStartDateChange = (startDate: Date) => {
    if (!startDate || !startDate.getDate() || startDate.getFullYear() <= MIN_YEAR) {
      setState((prevState) => ({
        ...prevState,
        startDate: null,
      }));
      return;
    }

    const startOfDay = new Date(startDate);
    startOfDay.setHours(0, 0, 0, 0);

    setState((prevState) => ({
      ...prevState,
      startDate: startOfDay,
    }));
  };

  const handleEndDateChange = (endDate: Date) => {
    if (!endDate || !endDate.getDate() || endDate.getFullYear() <= MIN_YEAR) {
      setState((prevState) => ({
        ...prevState,
        endDate: null,
      }));
      return;
    }

    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);

    setState((prevState) => ({
      ...prevState,
      endDate: endOfDay,
    }));
  };

  const handleResetClick = () => {
    setState(defaultState);
    props.handleApplyFilters(defaultActivityLogState);
  };

  const handleApplyFiltersClick = () => {
    props.handleApplyFilters({
      selectedActivityTypes: state.selectedActivityTypes,
      selectedContentTypes: state.selectedContentTypes,
      selectedGoalId: state.selectedGoalId === ActivityNameFilterDefaultTitle ? null : state.selectedGoalId,
      selectedStatuses: state.selectedStatuses,
      startDate: state.startDate,
      endDate: state.endDate,
    });
    props.modalService.closeModal(ActivityLogFilterModalId);
  };

  const getActivityTypeOptions = (): ELSDropDownOption[] => {
    const {
      recommendations,
      recommendationAttempts,
      quizAttemptDetails
    } = baseState;

    return getActiveAssignmentActivityTypeSettings(baseState)
      .map((activityType): ELSDropDownOption => {
        const attemptsInActivityType = recommendationAttempts.filter((attempt) => {
          const isStarted = attempt.status !== RecommendationAttemptStatusDto.NOT_STARTED;
          const recommendation = getRecommendationFromAttempt(recommendations, attempt);
          return recommendation.assessmentGoalType === activityType.activityType && isStarted;
        });

        if (activityType.activityType === RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE) {
          const quizSubmissionCount = getQuizSubmissionCount(recommendations, quizAttemptDetails, attemptsInActivityType);

          return {
            value: activityType.activityType,
            name: `${ActivityTypeConfigMap[activityType.activityType].title} (${quizSubmissionCount})`,
          };
        }

        return {
          value: activityType.activityType,
          name: `${ActivityTypeConfigMap[activityType.activityType].title} (${attemptsInActivityType.length})`,
        };
      });
  };

  const getContentTypeOptions = (): ELSDropDownOption[] => {
    const {
      recommendations,
      recommendationAttempts,
      quizAttemptDetails
    } = baseState;

    const contentTypes = getActiveContentTypes(baseState);

    return contentTypes.map((contentType) => {
      const attemptsInActivityType = recommendationAttempts.filter((attempt) => {
        const isStarted = attempt.status !== RecommendationAttemptStatusDto.NOT_STARTED;
        const recommendation = getRecommendationFromAttempt(recommendations, attempt);
        return recommendation.contentType === contentType && isStarted;
      });

      if (contentType === RecommendationContentTypeDto.RAAS_QUIZ) {
        const quizSubmissionCount = getQuizSubmissionCount(recommendations, quizAttemptDetails, attemptsInActivityType);

        return {
          value: contentType,
          name: `${RemediationContentTypeConfigMap[contentType].displayName} (${quizSubmissionCount > 0 ? quizSubmissionCount : 0})`,
        };
      }

      return {
        value: contentType,
        name: `${RemediationContentTypeConfigMap[contentType].displayName} (${attemptsInActivityType.length})`,
      };
    });
  };

  const getStatusOptions = (): ELSDropDownOption[] => {
    return Object.values(StatusWithDueDate)
      .filter((status) => status !== StatusWithDueDate.NOT_STARTED)
      .map((status) => ({
        value: status,
        name: StatusDisplayMap[status],
      }));
  };

  const getGoalDropDownOptions = (): ELSDropDownOption[] => {

    const {
      recommendations,
      eolsAssessment,
      recommendationAttempts
    } = baseState;

    const defaultGoalOption = {
      value: ActivityNameFilterDefaultTitle,
      name: ActivityNameFilterDefaultTitle,
    };

    const goals = getAssessmentGoals(eolsAssessment);

    const filterableGoals = goals.filter((goal) => {
      const attemptsInActivityType = recommendationAttempts.filter((attempt) => {
        const recommendation = getRecommendationFromAttempt(recommendations, attempt);
        const isStarted = attempt.status !== RecommendationAttemptStatusDto.NOT_STARTED;
        return goal.vtwId === recommendation.assessmentGoalVtwId && isStarted;
      });

      return attemptsInActivityType.length > 0;
    });

    if (filterableGoals.length === 0) {
      return [defaultGoalOption];
    }

    const options = filterableGoals.map((goal) => {
      const breadcrumbs = getGoalBreadcrumbs(goal, getCategoryGroupNameForGoal(goal, baseState));

      return {
        value: goal.vtwId,
        name: `${ActivityTypeConfigMap[goal.type].title}: ${breadcrumbs.pathFullDisplay}`,
      };
    });

    return [
      defaultGoalOption,
      ...options
    ];
  };

  const marginForExpandable = 'u-els-margin-bottom-1x';
  const chevronUp = 'chevron-up';
  const chevronDown = 'chevron-down';

  return (
    <div>
      <div className="c-els-modal">
        <div className="c-els-modal__window">
          <div className="u-els-padding-1x">
            <FlexLayout
              modifiers={[FlexLayoutModifier.SPACE_BETWEEN, FlexLayoutModifier.MIDDLE]}
            >
              <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                <span className="u-els-font-size-h3">
                  Filters
                </span>
              </FlexItem>

              <FlexItem>
                <button
                  type="button"
                  className="u-els-anchorize u-els-debuttonize"
                  onClick={() => handleCancelClick()}
                >
                  <ELSIcon name="close" size="1x"/>
                  <span className="u-els-hide-visually">Close</span>
                </button>
              </FlexItem>
            </FlexLayout>

            <div className="c-els-divider u-els-margin-top-1x u-els-margin-bottom-1x"/>

            <FlexLayout
              classes={[marginForExpandable]}
              modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.SPACE_BETWEEN]}
            >
              <FlexItem>
                <button
                  type="button"
                  className="u-els-debuttonize u-els-display-block u-els-color-secondary"
                  onClick={handleActivityTypeExpandToggle}
                  aria-expanded={state.isActivityTypeExpanded ? 'true' : 'false'}
                >
                  <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE]}>
                    <FlexItem>
                      <ELSIcon
                        size="1x1o2"
                        prefix="hmds"
                        name={state.isActivityTypeExpanded ? chevronUp : chevronDown}
                        customClass="u-els-display-block"
                      />
                    </FlexItem>
                    <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                      <span className="u-els-font-size-h4">
                        Activity Type
                      </span>
                    </FlexItem>
                  </FlexLayout>
                </button>
              </FlexItem>
              <FlexItem
                modifiers={[FlexLayoutModifier.GUTTERS]}
                isRender={state.selectedActivityTypes.length > 0}
              >
                <div className="c-nra-activity-log__filter-count">
                  {state.selectedActivityTypes.length}
                </div>
              </FlexItem>
            </FlexLayout>

            <IsRender isRender={state.isActivityTypeExpanded}>
              {
                getActivityTypeOptions()
                  .map((option) => {
                    return (
                      <div key={option.value} className="u-els-margin-bottom-1x u-els-margin-left-1o8">
                        <ELSCheckBox
                          changeHandler={() => {
                            handleActivityTypeFilterSelect(option.value as RemediationActivityTypeDto);
                          }}
                          checked={state.selectedActivityTypes.includes(option.value as RemediationActivityTypeDto)}
                        >
                          <span className="u-els-margin-left-1o2">
                            {option.name}
                          </span>
                        </ELSCheckBox>
                      </div>
                    );
                  })
              }
            </IsRender>

            <div className="c-els-divider u-els-margin-top-1x u-els-margin-bottom-1x"/>

            <FlexLayout
              classes={[marginForExpandable]}
              modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.SPACE_BETWEEN]}
            >
              <FlexItem>
                <button
                  type="button"
                  className="u-els-debuttonize u-els-display-block u-els-color-secondary"
                  onClick={handleContentTypeExpandToggle}
                  aria-expanded={state.isContentTypeExpanded ? 'true' : 'false'}
                >
                  <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE]}>
                    <FlexItem>
                      <ELSIcon
                        size="1x1o2"
                        prefix="hmds"
                        name={state.isContentTypeExpanded ? chevronUp : chevronDown}
                        customClass="u-els-display-block"
                      />
                    </FlexItem>
                    <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                      <span className="u-els-font-size-h4">
                        Content Type
                      </span>
                    </FlexItem>
                  </FlexLayout>
                </button>
              </FlexItem>
              <FlexItem
                modifiers={[FlexLayoutModifier.GUTTERS]}
                isRender={state.selectedContentTypes.length > 0}
              >
                <div className="c-nra-activity-log__filter-count">
                  {state.selectedContentTypes.length}
                </div>
              </FlexItem>
            </FlexLayout>

            <IsRender isRender={state.isContentTypeExpanded}>
              {
                getContentTypeOptions()
                  .map((option) => {
                    return (
                      <div key={option.value} className="u-els-margin-bottom-1x u-els-margin-left-1o8">
                        <ELSCheckBox
                          changeHandler={() => handleContentTypeFilterSelect(option.value as RecommendationContentTypeDto)}
                          checked={state.selectedContentTypes.includes(option.value as RecommendationContentTypeDto)}
                        >
                          <span className="u-els-margin-left-1o2">
                            {option.name}
                          </span>
                        </ELSCheckBox>
                      </div>
                    );
                  })
              }
            </IsRender>

            <div className="c-els-divider u-els-margin-top-1x u-els-margin-bottom-1x"/>

            <FlexLayout
              classes={[marginForExpandable]}
              modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.SPACE_BETWEEN]}
            >
              <FlexItem>
                <button
                  type="button"
                  className="u-els-debuttonize u-els-display-block u-els-color-secondary"
                  onClick={handleStatusExpandToggle}
                  aria-expanded={state.isStatusExpanded ? 'true' : 'false'}
                >
                  <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE]}>
                    <FlexItem>
                      <ELSIcon
                        size="1x1o2"
                        prefix="hmds"
                        name={state.isStatusExpanded ? chevronUp : chevronDown}
                        customClass="u-els-display-block"
                      />
                    </FlexItem>
                    <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                      <span className="u-els-font-size-h4">
                        Status
                      </span>
                    </FlexItem>
                  </FlexLayout>
                </button>
              </FlexItem>
              <FlexItem
                modifiers={[FlexLayoutModifier.GUTTERS]}
                isRender={state.selectedStatuses.length > 0}
              >
                <div className="c-nra-activity-log__filter-count">
                  {state.selectedStatuses.length}
                </div>
              </FlexItem>
            </FlexLayout>

            <IsRender isRender={state.isStatusExpanded}>
              {
                getStatusOptions()
                  .map((option) => {
                    return (
                      <div key={option.value} className="u-els-margin-bottom-1x u-els-margin-left-1o8">
                        <ELSCheckBox
                          changeHandler={() => handleStatusFilterSelect(option.value as StatusWithDueDate)}
                          checked={state.selectedStatuses.includes(option.value as StatusWithDueDate)}
                        >
                          <span className="u-els-margin-left-1o2">
                            {option.name}
                          </span>
                        </ELSCheckBox>
                      </div>
                    );
                  })
              }
            </IsRender>

            <div className="c-els-divider u-els-margin-top-1x u-els-margin-bottom-1x"/>

            <div>
              <div className="u-els-font-size-h4 u-els-margin-bottom-1o2">
                Activity Name
              </div>
              <ELSDropDown
                value={state.selectedGoalId}
                options={getGoalDropDownOptions()}
                changeHandler={(_, value) => handleActivityNameFilterSelect(value)}
              />
            </div>

            <div className="c-els-divider u-els-margin-top-1x u-els-margin-bottom-1x"/>

            <div>
              <div className="u-els-font-size-h4 u-els-margin-bottom-1o2">
                Date Submitted
              </div>
              <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.LEFT]}>
                <FlexItem>
                  <ELSDate
                    disableAfter={state.endDate}
                    value={state.startDate}
                    changeHandler={(_, val: Date) => handleStartDateChange(val)}
                  />
                </FlexItem>
                <FlexItem>
                  <div className="u-els-margin-left-1o2 u-els-margin-right-1o2">
                    to
                  </div>
                </FlexItem>
                <FlexItem>
                  <ELSDate
                    disableBefore={state.startDate}
                    value={state.endDate}
                    changeHandler={(_, val: Date) => handleEndDateChange(val)}
                  />
                </FlexItem>
              </FlexLayout>
            </div>

            <div className="c-els-divider u-els-margin-top-1x u-els-margin-bottom-1x"/>

            <FlexLayout modifiers={[FlexLayoutModifier.SPACE_BETWEEN]}>
              <FlexItem>
                <ELSButton
                  type={ELSButtonType.PRIMARY}
                  size={ELSButtonSize.DEFAULT}
                  onClick={() => handleApplyFiltersClick()}
                >
                  Apply Filters
                </ELSButton>
              </FlexItem>
              <FlexItem>
                <ELSButton
                  type={ELSButtonType.SECONDARY}
                  onClick={handleResetClick}
                >
                  Reset
                </ELSButton>
              </FlexItem>
            </FlexLayout>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogFilterModal;
